import type { TaggedNostrEvent } from "@snort/system"
import classNames from "classnames"
import { useCallback, useMemo, useRef, useState } from "react"
import { FormattedMessage, FormattedNumber } from "react-intl"

import Text from "@/Components/Text/Text"
import ProfilePreview from "@/Components/User/ProfilePreview"
import useImgProxy from "@/Hooks/useImgProxy"
import { findTag } from "@/Utils"

import { Markdown } from "./Markdown"
import { NoteProvider } from "./Note/NoteContext"
import { NoteContextMenu } from "./Note/NoteContextMenu"
import NoteFooter from "./Note/NoteFooter/NoteFooter"
import NoteTime from "./Note/NoteTime"

interface LongFormTextProps {
  ev: TaggedNostrEvent
  isPreview: boolean
  onClick?: () => void
  truncate?: boolean
}

const TEXT_TRUNCATE_LENGTH = 400

export function LongFormText(props: LongFormTextProps) {
  const title = findTag(props.ev, "title")
  const summary = findTag(props.ev, "summary")
  const image = findTag(props.ev, "image")
  const { proxy } = useImgProxy()
  const [reading, setReading] = useState(false)
  const readingRef = useRef(false)
  const [showMore, setShowMore] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  function previewText() {
    return (
      <Text
        id={props.ev.id}
        content={props.ev.content}
        tags={props.ev.tags}
        creator={props.ev.pubkey}
        truncate={props.isPreview ? 250 : undefined}
        disableLinkPreview={props.isPreview}
      />
    )
  }

  const readTime = useMemo(() => {
    const wpm = 225
    const words = props.ev.content.trim().split(/\s+/).length
    return {
      words,
      wpm,
      mins: Math.ceil(words / wpm),
    }
  }, [props.ev.content])

  const readAsync = useCallback((text: string) => {
    return new Promise<void>(resolve => {
      const ut = new SpeechSynthesisUtterance(text)
      ut.onend = () => {
        resolve()
      }
      window.speechSynthesis.speak(ut)
    })
  }, [])

  const readArticle = useCallback(async () => {
    if (ref.current && !readingRef.current) {
      readingRef.current = true
      setReading(true)
      const paragraphs = ref.current.querySelectorAll("p,h1,h2,h3,h4,h5,h6")
      for (const p of paragraphs) {
        if (p.textContent) {
          p.classList.add("reading")
          await readAsync(p.textContent)
          p.classList.remove("reading")
        }
      }
      readingRef.current = false
      setReading(false)
    }
  }, [readAsync])

  const stopReading = () => {
    setReading(false)
    if (ref.current) {
      const paragraphs = ref.current.querySelectorAll("p,h1,h2,h3,h4,h5,h6")
      paragraphs.forEach(a => a.classList.remove("reading"))
      window.speechSynthesis.cancel()
    }
  }

  const ToggleShowMore = () => (
    <a
      className="highlight cursor-pointer"
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
        setShowMore(!showMore)
      }}
    >
      {showMore ? <FormattedMessage defaultMessage="Show less" /> : <FormattedMessage defaultMessage="Show more" />}
    </a>
  )

  const shouldTruncate = props.truncate && props.ev.content.length > TEXT_TRUNCATE_LENGTH
  const content = shouldTruncate && !showMore ? props.ev.content.slice(0, TEXT_TRUNCATE_LENGTH) : props.ev.content

  function fullText() {
    return (
      <>
        <NoteFooter />
        <hr className="h-px my-1" />
        <div className="flex gap-2">
          <div>
            <FormattedMessage
              defaultMessage="{n} mins to read"
              values={{
                n: <FormattedNumber value={readTime.mins} />,
              }}
            />
          </div>
          <div>‧</div>
          {!reading && (
            <div className="cursor-pointer" onClick={() => readArticle()}>
              <FormattedMessage defaultMessage="Listen to this article" />
            </div>
          )}
          {reading && (
            <div className="cursor-pointer" onClick={() => stopReading()}>
              <FormattedMessage defaultMessage="Stop listening" />
            </div>
          )}
        </div>
        <hr className="h-px my-1" />
        {shouldTruncate && showMore && <ToggleShowMore />}
        <Markdown content={content} tags={props.ev.tags} ref={ref} className="font-[Georgia]" />
        {shouldTruncate && !showMore && <ToggleShowMore />}
        <hr className="h-px my-1" />
        <NoteFooter />
      </>
    )
  }

  return (
    <NoteProvider ev={props.ev}>
      <div
        className={classNames("flex flex-col gap-4 p-4 break-words leading-6", {
          "cursor-pointer": props.isPreview,
        })}
        onClick={props.onClick}
      >
        <ProfilePreview
          pubkey={props.ev.pubkey}
          actions={
            <div className="flex items-center gap-1">
              <NoteTime from={props.ev.created_at * 1000} />
              <NoteContextMenu />
            </div>
          }
          options={{
            about: false,
          }}
        />
        <h1 className="text-xl font-bold leading-10 m-0">{title}</h1>
        <small className="">{summary}</small>
        {image && <div className="h-[360px] bg-center bg-cover" style={{ backgroundImage: `url(${proxy(image)})` }} />}
        {props.isPreview ? previewText() : fullText()}
      </div>
    </NoteProvider>
  )
}
